import React from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { isEmpty, groupBy, concat, reduce } from 'lodash'
import { Redirect, useHistory } from 'react-router-dom'
import { PlanMappingForm } from 'components'
import { getMatchesForPlans, sortSelectOptions } from 'utils'
import { COMPANY_NAME } from 'config/portal'
import {
  NO_SERVICES_AGREEMENT_OPTION,
  ReasonForDiscard,
} from 'config/automatic-rollovers'

const propTypes = {
  participantsAgreementHash: PropTypes.object,
  setParticipantsAgreementHash: PropTypes.func.isRequired,
  participantsForSubmission: PropTypes.arrayOf(Types.rolloverParticipant)
    .isRequired,
  completedServicesAgreements: PropTypes.arrayOf(
    Types.servicesAgreementSummary
  ),
  setDiscardedParticipants: PropTypes.func.isRequired,
  discardedParticipants: PropTypes.arrayOf(Types.rolloverParticipant)
    .isRequired,
  updateParticipantsWithMappedAgreement: PropTypes.func.isRequired,
  uploadFilePath: PropTypes.string.isRequired,
  planConfirmationPath: PropTypes.string.isRequired,
  formType: PropTypes.string.isRequired,
  planMatchResults: PropTypes.arrayOf(Types.planMatchResult).isRequired,
}

const defaultProps = {
  participantsAgreementHash: null,
  completedServicesAgreements: null,
}

function PlanMappingView({
  setParticipantsAgreementHash,
  participantsAgreementHash,
  participantsForSubmission,
  completedServicesAgreements,
  setDiscardedParticipants,
  discardedParticipants,
  updateParticipantsWithMappedAgreement,
  uploadFilePath,
  planConfirmationPath,
  formType,
  planMatchResults,
}) {
  const history = useHistory()

  if (isEmpty(participantsForSubmission) && isEmpty(discardedParticipants)) {
    return <Redirect to={uploadFilePath} />
  }

  const msaAgreementIds = (planMatchResults || [])
    .filter(({ serviceAgreementId }) => Boolean(serviceAgreementId))
    .map(({ serviceAgreementId }) => serviceAgreementId)

  const servicesAgreementOptions = (completedServicesAgreements || [])
    .filter(
      ({ serviceAgreementID }) => !msaAgreementIds.includes(serviceAgreementID)
    )
    .map((servicesAgreement) => ({
      value: servicesAgreement.serviceAgreementID,
      label: servicesAgreement.name,
    }))
  const participantHash = groupBy(participantsForSubmission, 'planName')
  const servicesAgreementMatchesByPlan = getMatchesForPlans(
    participantHash,
    completedServicesAgreements || [],
    planMatchResults || []
  )
  const initialValues =
    participantsAgreementHash ||
    reduce(
      participantHash,
      (acc, participants, planName) => {
        const { exactMatch } = servicesAgreementMatchesByPlan[planName]
        const encodedPlanName = encodeURIComponent(planName).replaceAll(
          '.',
          '%2E'
        )
        acc[encodedPlanName] = {
          planName,
          servicesAgreement: exactMatch?.serviceAgreementID || '',
          participants,
          ...servicesAgreementMatchesByPlan[planName],
        }
        return acc
      },
      {}
    )
  const total = Object.keys(servicesAgreementMatchesByPlan).length

  const handleSubmit = (values) => {
    setParticipantsAgreementHash(values)
    updateParticipantsWithMappedAgreement(values)
    const missingPlanParticipantsData = reduce(
      values,
      (missingPlanParticipants, { servicesAgreement, participants }) => {
        if (servicesAgreement === NO_SERVICES_AGREEMENT_OPTION.value) {
          return concat(missingPlanParticipants, participants)
        }
        return missingPlanParticipants
      },
      []
    )
    setDiscardedParticipants({
      participants: missingPlanParticipantsData,
      reasonForDiscard: ReasonForDiscard.MISSING_AGREEMENT,
    })
    history.push({
      pathname: planConfirmationPath,
      state: { search: history.location.state?.search },
    })
  }

  return (
    <div className="card full-height upload-participant-file-view-container">
      <div className="form-masthead">
        <h2>Match your plans to agreements</h2>
        <p>
          We searched for possible matches between your plans and services
          agreements.{' '}
          <strong>
            Review the unmatched plans and select the corresponding services
            agreement on file with {COMPANY_NAME['short']}.{' '}
          </strong>
        </p>
      </div>
      <div
        className={`plan-mapping-container ${
          total > 10 ? 'scroll-enabled' : ''
        }`}
      >
        <div className="form-section">
          <PlanMappingForm
            formType={formType}
            initialValues={initialValues}
            selectOptions={sortSelectOptions(servicesAgreementOptions, 'label')}
            onSubmit={handleSubmit}
            servicesAgreements={completedServicesAgreements}
          />
        </div>
      </div>
    </div>
  )
}

PlanMappingView.propTypes = propTypes
PlanMappingView.defaultProps = defaultProps

export default PlanMappingView
